<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const isSaving = ref(false);

const commentReplyEnabled = ref(false);
const commentReplyMessage = ref('');
const followerWelcomeEnabled = ref(false);
const followerWelcomeMessage = ref('');

async function fetchConfig() {
  isLoading.value = true;
  try {
    const { data } = await InboxesAPI.getInstagramAutoReplyConfig(
      props.inbox.id
    );
    if (data.success && data.config) {
      const config = data.config;
      commentReplyEnabled.value = config.comment_reply?.enabled || false;
      commentReplyMessage.value = config.comment_reply?.message || '';
      followerWelcomeEnabled.value = config.follower_welcome?.enabled || false;
      followerWelcomeMessage.value = config.follower_welcome?.message || '';
    }
  } catch {
    useAlert(t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
}

async function saveConfig() {
  isSaving.value = true;
  try {
    const config = {
      comment_reply: {
        enabled: commentReplyEnabled.value,
        message: commentReplyMessage.value,
      },
      follower_welcome: {
        enabled: followerWelcomeEnabled.value,
        message: followerWelcomeMessage.value,
      },
    };
    const { data } = await InboxesAPI.updateInstagramAutoReplyConfig(
      props.inbox.id,
      config
    );
    if (data.success) {
      useAlert(t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SAVE_SUCCESS'));
    } else {
      useAlert(data.error || t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SAVE_ERROR'));
    }
  } catch {
    useAlert(t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

onMounted(() => {
  fetchConfig();
});
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.TITLE')"
      :sub-title="$t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SUBTITLE')"
    >
      <div v-if="isLoading" class="p-4 text-center">
        <span>{{ $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.LOADING') }}</span>
      </div>

      <div v-else class="space-y-8">
        <!-- Comment Auto-Reply Section -->
        <div class="space-y-4">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{
              $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.DESCRIPTION')
            }}
          </p>
          <div class="flex items-center gap-3">
            <input
              id="comment-reply-enabled"
              v-model="commentReplyEnabled"
              type="checkbox"
              class="w-4 h-4"
            />
            <label for="comment-reply-enabled" class="font-medium">
              {{
                $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.ENABLE_LABEL')
              }}
            </label>
          </div>
          <div v-if="commentReplyEnabled">
            <label class="block mb-2 text-sm font-medium">
              {{
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.MESSAGE_LABEL'
                )
              }}
            </label>
            <textarea
              v-model="commentReplyMessage"
              class="w-full min-h-[80px] p-2 border border-n-weak rounded-lg text-sm"
              :placeholder="
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.COMMENT_REPLY.MESSAGE_PLACEHOLDER'
                )
              "
              rows="3"
            />
          </div>
        </div>

        <!-- Follower Welcome Section -->
        <div class="space-y-4">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{
              $t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.DESCRIPTION')
            }}
          </p>
          <div class="flex items-center gap-3">
            <input
              id="follower-welcome-enabled"
              v-model="followerWelcomeEnabled"
              type="checkbox"
              class="w-4 h-4"
            />
            <label for="follower-welcome-enabled" class="font-medium">
              {{
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.ENABLE_LABEL'
                )
              }}
            </label>
          </div>
          <div v-if="followerWelcomeEnabled">
            <label class="block mb-2 text-sm font-medium">
              {{
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.MESSAGE_LABEL'
                )
              }}
            </label>
            <textarea
              v-model="followerWelcomeMessage"
              class="w-full min-h-[80px] p-2 border border-n-weak rounded-lg text-sm"
              :placeholder="
                $t(
                  'INBOX_MGMT.INSTAGRAM_AUTO_REPLY.FOLLOWER_WELCOME.MESSAGE_PLACEHOLDER'
                )
              "
              rows="3"
            />
          </div>
        </div>

        <!-- Save Button -->
        <div class="flex justify-end pt-4">
          <NextButton
            :is-loading="isSaving"
            :label="$t('INBOX_MGMT.INSTAGRAM_AUTO_REPLY.SAVE')"
            @click="saveConfig"
          />
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
